// Capture evidence with the device's own camera, from the issue page.
//
// The issue that needs a photo is usually being looked at on site, on a
// phone, by someone who then has to leave the app, open the camera, come
// back and hunt for the file. This takes the picture (or the video) in
// place, uploads it, and puts it straight on the canvas.
//
// Every camera the device exposes is offered — a phone's front and back,
// a laptop's built-in and any USB webcam — because "the camera" on a
// phone is the wrong one about half the time. Labels are only readable
// after permission has been granted once, so the list is re-enumerated
// after the first stream starts.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal, Button, Select, Alert, Space, Tooltip } from "antd";
import {
  CameraOutlined, VideoCameraOutlined, StopOutlined, SwapOutlined, AudioMutedOutlined, AudioOutlined,
} from "@ant-design/icons";
import { T } from "pages/Issue/theme";

// webm is what MediaRecorder gives everywhere except Safari, which gives
// mp4; ask for what the browser actually supports rather than assuming.
function pickVideoMime() {
  const candidates = [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ];
  if (typeof MediaRecorder === "undefined") return "";
  return candidates.find((m) => { try { return MediaRecorder.isTypeSupported(m); } catch (e) { return false; } }) || "";
}

const secs = (ms) => {
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

export default function IssueCameraCapture({ open, onClose, onPhoto, onVideo }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState("");
  const [withAudio, setWithAudio] = useState(true);
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const stop = useCallback(() => {
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch (e) { /* noop */ }
    recorderRef.current = null;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    const s = streamRef.current;
    if (s) s.getTracks().forEach((t) => { try { t.stop(); } catch (e) { /* noop */ } });
    streamRef.current = null;
    setRecording(false);
    setElapsed(0);
  }, []);

  const start = useCallback(async (id) => {
    setErr("");
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setErr("This browser exposes no camera API. On iOS and Android the page must be served over HTTPS.");
      return;
    }
    try {
      const s = await navigator.mediaDevices.getUserMedia({
        video: id ? { deviceId: { exact: id } } : { facingMode: { ideal: "environment" } },
        audio: withAudio,
      });
      const old = streamRef.current;
      if (old) old.getTracks().forEach((t) => { try { t.stop(); } catch (e) { /* noop */ } });
      streamRef.current = s;
      if (videoRef.current) {
        videoRef.current.srcObject = s;
        try { await videoRef.current.play(); } catch (e) { /* autoplay guard */ }
      }
      // Labels are blank until permission is granted, so list again now.
      const all = await navigator.mediaDevices.enumerateDevices();
      const cams = all.filter((d) => d.kind === "videoinput");
      setDevices(cams);
      const active = s.getVideoTracks()[0];
      const settings = active && active.getSettings ? active.getSettings() : {};
      setDeviceId(id || settings.deviceId || (cams[0] && cams[0].deviceId) || "");
    } catch (e) {
      setErr(
        e && e.name === "NotAllowedError"
          ? "The browser blocked camera access — allow it for this site and try again."
          : e && e.name === "NotFoundError"
            ? "No camera found on this device."
            : e?.message || "The camera could not be opened"
      );
    }
  }, [withAudio]);

  useEffect(() => {
    if (!open) { stop(); return undefined; }
    start("");
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const takePhoto = async () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    setBusy("photo");
    try {
      const c = document.createElement("canvas");
      c.width = v.videoWidth;
      c.height = v.videoHeight;
      c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
      const dataUrl = c.toDataURL("image/png");
      await onPhoto(dataUrl);
      onClose();
    } catch (e) {
      setErr(e?.message || "The photo could not be saved");
    } finally {
      setBusy("");
    }
  };

  const startRecording = () => {
    const s = streamRef.current;
    if (!s || typeof MediaRecorder === "undefined") {
      setErr("This browser cannot record video.");
      return;
    }
    chunksRef.current = [];
    const mimeType = pickVideoMime();
    try {
      const rec = new MediaRecorder(s, mimeType ? { mimeType } : undefined);
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        const type = mimeType || "video/webm";
        const blob = new Blob(chunksRef.current, { type });
        chunksRef.current = [];
        if (!blob.size) return;
        setBusy("video");
        try {
          const ext = /mp4/.test(type) ? "mp4" : "webm";
          const file = new File([blob], `capture-${Date.now()}.${ext}`, { type });
          await onVideo(file);
          onClose();
        } catch (e) {
          setErr(e?.message || "The recording could not be uploaded");
        } finally {
          setBusy("");
        }
      };
      rec.start(1000);
      recorderRef.current = rec;
      setRecording(true);
      const began = Date.now();
      timerRef.current = setInterval(() => setElapsed(Date.now() - began), 500);
    } catch (e) {
      setErr(e?.message || "Recording could not be started");
    }
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
    try { if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop(); } catch (e) { /* noop */ }
  };

  return (
    <Modal
      open={open}
      onCancel={() => { if (!recording) onClose(); }}
      title="Camera"
      footer={null}
      width={720}
      destroyOnClose
      maskClosable={!recording}
    >
      {err && <Alert type="warning" showIcon message={err} style={{ marginBottom: 10 }} />}

      <div style={{ position: "relative", background: "#111", borderRadius: 6, overflow: "hidden" }}>
        <video ref={videoRef} muted playsInline autoPlay style={{ width: "100%", maxHeight: 420, display: "block", background: "#000" }} />
        {recording && (
          <div style={{
            position: "absolute", top: 10, left: 10, background: "rgba(197,48,48,.9)", color: "#fff",
            borderRadius: 4, padding: "2px 8px", fontSize: 12, display: "flex", alignItems: "center", gap: 6,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff" }} />
            REC {secs(elapsed)}
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
        <Select
          size="small"
          style={{ minWidth: 240 }}
          value={deviceId || undefined}
          placeholder={devices.length ? "Camera" : "Looking for cameras…"}
          disabled={recording}
          onChange={(v) => { setDeviceId(v); start(v); }}
          options={devices.map((d, i) => ({
            value: d.deviceId,
            label: d.label || `Camera ${i + 1}`,
          }))}
          suffixIcon={<SwapOutlined />}
        />
        <Tooltip title={withAudio ? "Recording with sound" : "Recording muted"}>
          <Button
            size="small"
            icon={withAudio ? <AudioOutlined /> : <AudioMutedOutlined />}
            disabled={recording}
            onClick={() => { setWithAudio((v) => !v); start(deviceId); }}
          />
        </Tooltip>
        <span style={{ flex: 1 }} />
        <Space>
          <Button
            icon={<CameraOutlined />}
            onClick={takePhoto}
            loading={busy === "photo"}
            disabled={recording || !!busy}
          >
            Take a picture
          </Button>
          {recording ? (
            <Button danger icon={<StopOutlined />} onClick={stopRecording} loading={busy === "video"}>
              Stop &amp; save
            </Button>
          ) : (
            <Button type="primary" style={{ background: T.navy, borderColor: T.navy }}
                    icon={<VideoCameraOutlined />} onClick={startRecording} disabled={!!busy}>
              Record a video
            </Button>
          )}
        </Space>
      </div>
      <div style={{ fontSize: 11.5, color: T.muted, marginTop: 8 }}>
        A picture becomes the issue's image, ready to mark up. A recording is
        attached and plays on the canvas. Both upload as soon as you finish.
      </div>
    </Modal>
  );
}
